import { apiClient } from './apiClient';

export interface MissingTimesheet {
  id: string;
  employeeId: number;
  employeeName: string;
  employeeEmail: string;
  department: string;
  managerId?: number;
  managerName?: string;
  weekEnding: string;
  status: 'Missing' | 'Draft' | 'Rejected';
  daysOverdue: number;
  severity: 'grace' | 'warning' | 'urgent' | 'critical';
  expectedHours: number;
  submittedHours: number;
  missingDays?: string[];
  lastActivity?: string;
  lastReminded?: string;
  photoUrl?: string;
}

export interface MissingTimesheetSummary {
  totalMissing: number;
  uniqueEmployees: number;
  complianceRate: number;
  avgDaysOverdue: number;
  repeatOffenders: number;
  severityBreakdown: {
    grace: { count: number; percentage: number };
    warning: { count: number; percentage: number };
    urgent: { count: number; percentage: number };
    critical: { count: number; percentage: number };
  };
  // Comparison with previous period
  totalMissingChange: number;
  complianceRateChange: number;
  avgDaysOverdueChange: number;
  repeatOffendersChange: number;
}

export interface PagedMissingTimesheetResponse {
  data: MissingTimesheet[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface MissingTimesheetQueryParams {
  dateFrom?: Date;
  dateTo?: Date;
  departments?: string[];
  managerIds?: string[];
  severities?: string[];
  statuses?: string[];
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: string;
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function buildQueryString(params: MissingTimesheetQueryParams): string {
  const queryParts: string[] = [];
  
  if (params.dateFrom) {
    queryParts.push(`dateFrom=${formatDate(params.dateFrom)}`);
  }
  if (params.dateTo) {
    queryParts.push(`dateTo=${formatDate(params.dateTo)}`);
  }
  if (params.departments && params.departments.length > 0) {
    queryParts.push(`departments=${params.departments.join(',')}`);
  }
  if (params.managerIds && params.managerIds.length > 0) {
    queryParts.push(`managerIds=${params.managerIds.join(',')}`);
  }
  if (params.severities && params.severities.length > 0) {
    queryParts.push(`severities=${params.severities.join(',')}`);
  }
  if (params.statuses && params.statuses.length > 0) {
    queryParts.push(`statuses=${params.statuses.join(',')}`);
  }
  if (params.pageNumber) {
    queryParts.push(`pageNumber=${params.pageNumber}`);
  }
  if (params.pageSize) {
    queryParts.push(`pageSize=${params.pageSize}`);
  }
  if (params.sortBy) {
    queryParts.push(`sortBy=${params.sortBy}`);
  }
  if (params.sortDir) {
    queryParts.push(`sortDir=${params.sortDir}`);
  }
  
  return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
}

export const missingTimesheetsApi = {
  async getMissingTimesheets(params: MissingTimesheetQueryParams = {}): Promise<PagedMissingTimesheetResponse> {
    const queryString = buildQueryString(params);
    return apiClient.get<PagedMissingTimesheetResponse>(`/reports/missing-timesheets${queryString}`);
  },

  async getSummary(params: Omit<MissingTimesheetQueryParams, 'pageNumber' | 'pageSize' | 'sortBy' | 'sortDir'> = {}): Promise<MissingTimesheetSummary> {
    const queryString = buildQueryString(params);
    return apiClient.get<MissingTimesheetSummary>(`/reports/missing-timesheets/summary${queryString}`);
  },

  async sendReminders(employeeIds: number[], weekEnding: string): Promise<{ message: string; count: number }> {
    return apiClient.post<{ message: string; count: number }, { employeeIds: number[]; weekEnding: string }>(
      '/reports/missing-timesheets/send-reminders',
      { employeeIds, weekEnding }
    );
  },
};
